import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import {
  getLocaleCode,
  getSiteSeo,
  normalizeLanguage
} from "../../seo/siteSeo";

const DEFAULT_IMAGE_PATH = "/assets/img/smileboss.png";
const PERSON_URLS = [
  "https://www.facebook.com/stephane.kamwa",
  "https://www.instagram.com/stephanekamwa_theplug",
  "https://www.linkedin.com/in/stephane-tchuente-kamwa-532b73185/",
  "https://wa.me/+4915752069252/",
  "https://www.youtube.com/@THEPLUG_GROUP"
];

function getOrigin() {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.origin;
}

function getCurrentPath() {
  if (typeof window === "undefined") {
    return "/";
  }

  return window.location.pathname || "/";
}

function toAbsoluteUrl(value, origin, fallbackPath = "/") {
  if (!value) {
    return origin ? new URL(fallbackPath, origin).toString() : fallbackPath;
  }

  try {
    return new URL(value).toString();
  } catch (error) {
    const normalizedPath = String(value).startsWith("/")
      ? String(value)
      : `/${String(value)}`;

    return origin ? new URL(normalizedPath, origin).toString() : normalizedPath;
  }
}

function PageSeo({
  title,
  description,
  keywords,
  canonicalPath,
  image,
  imageAlt,
  type = "website",
  noindex = false,
  publishedTime,
  structuredData
}) {
  const { i18n } = useTranslation("common");
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const siteSeo = getSiteSeo(language);
  const origin = getOrigin();
  const currentPath = canonicalPath || getCurrentPath();
  const canonicalUrl = toAbsoluteUrl(currentPath, origin, "/");
  const imageUrl = toAbsoluteUrl(image || DEFAULT_IMAGE_PATH, origin, DEFAULT_IMAGE_PATH);
  const fullTitle = title ? `${title} | ${siteSeo.siteName}` : siteSeo.siteName;
  const metaDescription = description || siteSeo.defaultDescription;
  const metaKeywords = keywords || siteSeo.defaultKeywords;
  const robots = noindex ? "noindex, nofollow" : "index, follow";

  const basePersonSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: siteSeo.siteName,
    jobTitle: siteSeo.personTitle,
    description: siteSeo.defaultDescription,
    url: origin || canonicalUrl,
    image: imageUrl,
    email: "hello@stephanekamwa.de",
    sameAs: PERSON_URLS
  };

  const pageSchema =
    type === "article"
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: fullTitle,
          description: metaDescription,
          image: imageUrl,
          inLanguage: language,
          mainEntityOfPage: canonicalUrl,
          author: {
            "@type": "Person",
            name: siteSeo.siteName,
            url: origin || canonicalUrl
          },
          ...(publishedTime ? { datePublished: publishedTime } : {})
        }
      : {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: fullTitle,
          description: metaDescription,
          url: canonicalUrl,
          inLanguage: language,
          image: imageUrl
        };

  const extraStructuredData = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : [];

  const allStructuredData = [basePersonSchema, pageSchema, ...extraStructuredData];

  return (
    <Helmet htmlAttributes={{ lang: language }}>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={siteSeo.siteName} />
      <meta name="robots" content={robots} />
      <meta property="og:locale" content={getLocaleCode(language)} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:site_name" content={siteSeo.siteName} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={imageAlt || siteSeo.siteName} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      <link rel="canonical" href={canonicalUrl} />
      {allStructuredData.map((entry, index) => (
        <script key={`jsonld-${index}`} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}

export default PageSeo;
